/**
 * ChatWidget — E5: Vito Sales Chatbot Widget
 *
 * Features:
 *   - Floating glowing button (all pages, bottom-right)
 *   - Full conversation UI (bubbles, timestamps, typing indicator)
 *   - Session persistence via localStorage (24h TTL)
 *   - REST polling for real-time responses
 *   - Auto-open on sales pages after 15s delay
 *   - UTM parameter capture from URL
 *   - Mobile-responsive
 *
 * Related: agent-ops #52 (E5), specs/sales-chatbot-bmad.md §8 Epic 7
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, MessageCircle, Minimize2 } from 'lucide-react';
import { createChatSession, resumeChatSession, sendChatMessage } from '../../services/chatApi.js';

// Pages that trigger auto-open after 15s
const SALES_PAGES = ['/', '/product', '/why', '/demo', '/pricing'];

// localStorage key
const SESSION_STORAGE_KEY = 'vito_chat_session';

// How long to show typing indicator before giving up (ms)
const RESPONSE_TIMEOUT_MS = 15000;

export default function ChatWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('active');
  const [hasUnread, setHasUnread] = useState(false);
  const [autoOpenFired, setAutoOpenFired] = useState(false);
  const [error, setError] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const autoOpenTimerRef = useRef(null);

  // ── Session Management ────────────────────────────────────

  const loadOrCreateSession = useCallback(async () => {
    const stored = getStoredSession();

    if (stored) {
      // Try to resume
      try {
        const resumed = await resumeChatSession(stored.token);
        if (resumed.ok) {
          setSessionToken(stored.token);
          setSessionStatus(resumed.session.status);
          // Restore message history from localStorage
          if (stored.messages && stored.messages.length > 0) {
            setMessages(stored.messages);
          } else {
            // Session resumed but no local messages — add Vito's opener
            setMessages([vitoOpener()]);
          }
          return;
        }
      } catch {
        // Session expired/invalid — fall through to create new
      }
    }

    // Create new session
    try {
      const utmParams = captureUtmParams();
      const created = await createChatSession({
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        referrer: document.referrer || null,
        page_url: window.location.href,
      });

      if (created.ok) {
        const newToken = created.session_token;
        setSessionToken(newToken);
        setSessionStatus('active');
        const opener = vitoOpener();
        setMessages([opener]);
        storeSession(newToken, [opener]);
      }
    } catch (err) {
      console.error('ChatWidget: session create failed', err);
      setError('Unable to start chat. Please try again.');
    }
  }, []);

  // Initialize session when widget first opens
  useEffect(() => {
    if (isOpen && !sessionToken) {
      loadOrCreateSession();
    }
  }, [isOpen, sessionToken, loadOrCreateSession]);

  // ── Auto-open on sales pages ──────────────────────────────

  useEffect(() => {
    if (autoOpenFired) return;
    if (!SALES_PAGES.includes(location.pathname)) return;
    // Don't auto-open if user has already interacted
    if (getStoredSession()) return;

    autoOpenTimerRef.current = setTimeout(() => {
      setIsOpen(true);
      setAutoOpenFired(true);
    }, 15000);

    return () => {
      if (autoOpenTimerRef.current) clearTimeout(autoOpenTimerRef.current);
    };
  }, [location.pathname, autoOpenFired]);

  // ── Scroll to bottom on new messages ─────────────────────

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
    }
  }, [messages, isTyping, isOpen, isMinimized]);

  // ── Unread badge ──────────────────────────────────────────

  useEffect(() => {
    if (!isOpen && messages.length > 0 && messages[messages.length - 1].role === 'vito') {
      setHasUnread(true);
    }
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen) setHasUnread(false);
  }, [isOpen]);

  // ── Message Sending ───────────────────────────────────────

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading || !sessionToken) return;
    if (sessionStatus === 'closed') return;

    setError(null);
    setInputValue('');

    // Optimistically add visitor message
    const visitorMsg = {
      id: `local-${Date.now()}`,
      role: 'visitor',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => {
      const updated = [...prev, visitorMsg];
      storeSession(sessionToken, updated);
      return updated;
    });

    setIsTyping(true);
    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      setIsTyping(false);
      setIsLoading(false);
      setError('Vito is thinking... try again in a moment.');
    }, RESPONSE_TIMEOUT_MS);

    try {
      const result = await sendChatMessage(sessionToken, text);
      clearTimeout(timeoutId);
      setIsTyping(false);
      setIsLoading(false);

      if (result.ok) {
        const vitoMsg = {
          id: result.response.id || `vito-${Date.now()}`,
          role: 'vito',
          content: result.response.content,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => {
          const updated = [...prev, vitoMsg];
          storeSession(sessionToken, updated);
          return updated;
        });
        if (result.session?.status) {
          setSessionStatus(result.session.status);
        }
      } else {
        setError(result.error || 'Something went wrong. Try again.');
      }
    } catch (err) {
      clearTimeout(timeoutId);
      setIsTyping(false);
      setIsLoading(false);
      setError('Connection issue. Please try again.');
    }
  }, [inputValue, isLoading, sessionToken, sessionStatus]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Open/close ────────────────────────────────────────────

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    if (autoOpenTimerRef.current) clearTimeout(autoOpenTimerRef.current);
    // Focus input after animation
    setTimeout(() => inputRef.current?.focus(), 300);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ── Helpers ───────────────────────────────────────────────

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const isExpired = sessionStatus === 'closed';
  const canSend = !isLoading && !isExpired && sessionToken;

  // ── Render ────────────────────────────────────────────────

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          aria-label="Open Vito chat"
          className="chat-widget-button fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-5 py-3 font-heading font-semibold text-sm shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7B61FF]"
          style={{
            background: 'linear-gradient(135deg, #7B61FF 0%, #5B3FD9 100%)',
            color: '#F0EFF4',
            boxShadow: '0 0 24px rgba(123,97,255,0.45), 0 4px 20px rgba(0,0,0,0.4)',
          }}
        >
          <MessageCircle size={18} />
          <span>Talk to Vito</span>
          {hasUnread && (
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-[#0A0A14]" />
          )}
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="chat-widget-panel fixed bottom-6 right-6 z-50 flex flex-col rounded-2xl overflow-hidden shadow-2xl"
          style={{
            width: 'min(380px, calc(100vw - 24px))',
            height: isMinimized ? 'auto' : 'min(580px, calc(100vh - 100px))',
            background: '#0F0F1A',
            border: '1px solid rgba(123,97,255,0.25)',
            boxShadow: '0 0 40px rgba(123,97,255,0.15), 0 20px 60px rgba(0,0,0,0.6)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0 cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #1A1A2E 0%, #16162A 100%)',
              borderBottom: '1px solid rgba(123,97,255,0.2)',
            }}
            onClick={isMinimized ? handleMaximize : undefined}
          >
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center text-base font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #7B61FF, #5B3FD9)',
                    boxShadow: '0 0 12px rgba(123,97,255,0.5)',
                  }}
                >
                  V
                </div>
                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-400 ring-2 ring-[#1A1A2E]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#F0EFF4] leading-tight">Vito</p>
                <p className="text-[10px] text-[#7B61FF] leading-tight">RoofClaw AI · Online</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!isMinimized && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
                  className="p-1.5 rounded-lg text-[#8888AA] hover:text-[#F0EFF4] hover:bg-white/5 transition-colors"
                  aria-label="Minimize"
                >
                  <Minimize2 size={14} />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
                className="p-1.5 rounded-lg text-[#8888AA] hover:text-[#F0EFF4] hover:bg-white/5 transition-colors"
                aria-label="Close chat"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Messages + Input (collapsed when minimized) */}
          {!isMinimized && (
            <>
              {/* Messages Area */}
              <div
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#7B61FF33 transparent' }}
              >
                {messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}

                {isTyping && <TypingIndicator />}

                {error && (
                  <div className="text-center">
                    <p className="text-xs text-red-400 bg-red-950/40 rounded-lg px-3 py-2 inline-block">
                      {error}
                    </p>
                  </div>
                )}

                {isExpired && (
                  <div className="text-center">
                    <p className="text-xs text-[#8888AA] bg-white/5 rounded-lg px-3 py-2 inline-block">
                      Session ended. Refresh to start a new conversation.
                    </p>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div
                className="flex-shrink-0 px-3 py-3"
                style={{ borderTop: '1px solid rgba(123,97,255,0.15)' }}
              >
                <div
                  className="flex items-end gap-2 rounded-xl px-3 py-2"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(123,97,255,0.2)' }}
                >
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isExpired ? 'Session ended' : "Type a message…"}
                    disabled={!canSend}
                    rows={1}
                    className="flex-1 bg-transparent text-[#F0EFF4] text-sm placeholder-[#55556A] resize-none outline-none leading-relaxed"
                    style={{ maxHeight: '96px', minHeight: '24px' }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!canSend || !inputValue.trim()}
                    aria-label="Send message"
                    className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                    style={{
                      background: canSend && inputValue.trim()
                        ? 'linear-gradient(135deg, #7B61FF, #5B3FD9)'
                        : 'rgba(123,97,255,0.2)',
                      boxShadow: canSend && inputValue.trim() ? '0 0 12px rgba(123,97,255,0.4)' : 'none',
                    }}
                  >
                    <Send size={14} color="#F0EFF4" />
                  </button>
                </div>
                <p className="text-center text-[10px] text-[#44445A] mt-1.5">
                  Powered by RoofClaw AI · 
                  <span className="ml-1 text-[#7B61FF]">Not a form. A conversation.</span>
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────

function MessageBubble({ message }) {
  const isVito = message.role === 'vito';
  const time = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className={`flex gap-2 ${isVito ? 'justify-start' : 'justify-end'}`}>
      {isVito && (
        <div
          className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold self-end mb-1"
          style={{ background: 'linear-gradient(135deg, #7B61FF, #5B3FD9)' }}
        >
          V
        </div>
      )}
      <div className={`flex flex-col gap-1 max-w-[80%] ${isVito ? 'items-start' : 'items-end'}`}>
        <div
          className="px-3 py-2 rounded-2xl text-sm leading-relaxed"
          style={
            isVito
              ? {
                  background: 'rgba(123,97,255,0.12)',
                  border: '1px solid rgba(123,97,255,0.2)',
                  color: '#E8E7F0',
                  borderBottomLeftRadius: '6px',
                }
              : {
                  background: 'linear-gradient(135deg, #7B61FF, #5B3FD9)',
                  color: '#F0EFF4',
                  borderBottomRightRadius: '6px',
                }
          }
        >
          {message.content}
        </div>
        <span className="text-[10px] text-[#44445A] px-1">{time}</span>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-2 justify-start">
      <div
        className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold"
        style={{ background: 'linear-gradient(135deg, #7B61FF, #5B3FD9)' }}
      >
        V
      </div>
      <div
        className="px-3 py-3 rounded-2xl flex items-center gap-1"
        style={{
          background: 'rgba(123,97,255,0.12)',
          border: '1px solid rgba(123,97,255,0.2)',
          borderBottomLeftRadius: '6px',
        }}
      >
        <span className="typing-dot" style={{ animationDelay: '0ms' }} />
        <span className="typing-dot" style={{ animationDelay: '160ms' }} />
        <span className="typing-dot" style={{ animationDelay: '320ms' }} />
      </div>
    </div>
  );
}

// ── Utilities ─────────────────────────────────────────────────

function vitoOpener() {
  return {
    id: 'vito-opener',
    role: 'vito',
    content:
      "Hey, I'm Vito — RoofClaw's AI. Full disclosure: I'm artificial, but my intelligence is very real. I help roofing companies figure out if they're ready for an AI employee. What should I call you?",
    timestamp: new Date().toISOString(),
  };
}

function captureUtmParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
  };
}

function getStoredSession() {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Check 24h TTL
    const age = Date.now() - (data.created_at || 0);
    if (age > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function storeSession(token, messages) {
  try {
    const existing = getStoredSession();
    localStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({
        token,
        messages,
        created_at: existing?.created_at || Date.now(),
        updated_at: Date.now(),
      })
    );
  } catch {
    // localStorage unavailable — silently skip
  }
}
